import { LYRIC_LINE_LEAD_SECONDS } from 'fym-shared/src/music24/lyricTiming';
import { clipAt, maxClipSpanSec, type MusicVideoClip, type MusicVideoDoc } from 'fym-shared/src/music24/musicVideoDoc';
import type { MusicVideoLyricLine, MusicVideoSongMapSection } from 'fym-shared/src/music24/musicVideoApi';
import { musicVideoTextTimes, type MusicVideoTextSpan } from 'fym-shared/src/music24/musicVideoTexts';
import { MIN_LOOP_SOURCE_SEC } from '../doc/clipOps';
import { isChorus } from '../doc/sectionOps';

/**
 * The pure maths of the live preview (spec §4.6). The master audio is the one clock: every frame
 * reads its position, and the two muted `<video>` elements follow it. Nothing here touches the DOM.
 */

/** A video further than this from its place seeks again (spec §4.6: resync at 0.15 s drift). */
export const PREVIEW_RESYNC_SEC = 0.15;
/** A caption line with no end and no next line stays this long. */
export const CAPTION_FALLBACK_SEC = 4;

/** The second of the footage that shows at `songSec` of the song: `inSec + (t - startSec) * speed`. */
export function clipSourceTime(clip: Pick<MusicVideoClip, 'inSec' | 'startSec' | 'speed'>, songSec: number): number {
  return Math.max(0, clip.inSec + (songSec - clip.startSec) * clip.speed);
}

/**
 * The second of the footage at `songSec` when the clip may be longer than the rest of its source
 * (a cut that the director wrote, an older cut). The server plan builder plays such a clip as a loop
 * (`clipLoopParts`): from `inSec` to the end of the source, then again from in point 0 for
 * `sourceSec / speed` each time. The preview shows the same loop, not a held last frame. With no
 * known length, or a source under `MIN_LOOP_SOURCE_SEC`, it is `clipSourceTime`. Pure.
 */
export function loopedSourceTime(
  clip: Pick<MusicVideoClip, 'inSec' | 'startSec' | 'speed'>,
  songSec: number,
  sourceSec: number | null
): number {
  if (sourceSec === null || !Number.isFinite(sourceSec) || sourceSec < MIN_LOOP_SOURCE_SEC || !(clip.speed > 0)) {
    return clipSourceTime(clip, songSec);
  }
  const elapsed = Math.max(0, songSec - clip.startSec);
  // The first part keeps the in point while at least one millisecond of the source is left (the server rule).
  const firstSpan = maxClipSpanSec(sourceSec, clip.inSec, clip.speed);
  const firstFrom = firstSpan >= 0.001 ? clip.inSec : 0;
  const firstLength = firstSpan >= 0.001 ? firstSpan : sourceSec / clip.speed;
  if (elapsed < firstLength) {
    return firstFrom + elapsed * clip.speed;
  }
  const loopLength = sourceSec / clip.speed;
  return ((elapsed - firstLength) % loopLength) * clip.speed;
}

/** True when a video is so far from its place that it must seek. */
export function needsResync(videoSec: number, wantedSec: number): boolean {
  return Math.abs(videoSec - wantedSec) > PREVIEW_RESYNC_SEC;
}

/** The clip on screen at `songSec`, and the clip after it (the second video loads it). */
export function previewClips(
  doc: Pick<MusicVideoDoc, 'clips'>,
  songSec: number
): { current: MusicVideoClip | null; next: MusicVideoClip | null } {
  const current = clipAt(doc, songSec);
  if (current === null) {
    return { current: null, next: doc.clips[0] ?? null };
  }
  const index = doc.clips.indexOf(current);
  return { current, next: doc.clips[index + 1] ?? null };
}

/**
 * The lyric line on screen at `songSec` (design d1). A line shows `leadSec` before its start
 * (`LYRIC_LINE_LEAD_SECONDS`, the rule of every player) until its end, else until the next line,
 * else for `CAPTION_FALLBACK_SEC`. With `when: 'choruses'` only the lines that start inside a chorus
 * of the song map show. A line with no time never shows.
 */
export function captionAt(
  lines: readonly MusicVideoLyricLine[],
  songSec: number,
  options: { when: 'all' | 'choruses'; sections: readonly MusicVideoSongMapSection[]; leadSec?: number }
): MusicVideoLyricLine | null {
  const ms = (songSec + (options.leadSec ?? LYRIC_LINE_LEAD_SECONDS)) * 1000;
  const timed = lines.filter((line) => line.startMs !== null);
  for (let index = timed.length - 1; index >= 0; index -= 1) {
    const line = timed[index];
    if (line !== undefined && line.startMs !== null && line.startMs <= ms) {
      const next = timed[index + 1];
      const endMs = line.endMs ?? next?.startMs ?? line.startMs + CAPTION_FALLBACK_SEC * 1000;
      if (ms >= endMs) {
        return null;
      }
      if (options.when === 'choruses') {
        const startSec = line.startMs / 1000;
        const inChorus = options.sections.some(
          (section) => isChorus(section) && startSec >= section.startSec && startSec < section.endSec
        );
        return inChorus ? line : null;
      }
      return line;
    }
  }
  return null;
}

/** When the fixed texts show, in seconds of the song. */
export interface TextSpans {
  title: MusicVideoTextSpan;
  name: MusicVideoTextSpan;
  /** Null for a video too short for an end card (under 40 s). */
  endCard: MusicVideoTextSpan | null;
}

/**
 * The places of the title, the name and the end card: the shared rule `musicVideoTextTimes` of
 * fym-shared, which the render uses too. The rule reads and answers video seconds
 * (0 = `settings.startSec`); the preview works in song seconds, so this moves the downbeats in and
 * the spans out.
 */
export function textSpans(settings: { startSec: number; endSec: number }, downbeats: readonly number[]): TextSpans {
  const videoDownbeats = downbeats.map((sec) => sec - settings.startSec).filter((sec) => sec >= 0);
  const times = musicVideoTextTimes({ downbeats: videoDownbeats, lengthSec: settings.endSec - settings.startSec });
  const toSong = (span: MusicVideoTextSpan): MusicVideoTextSpan => ({
    startSec: settings.startSec + span.startSec,
    endSec: settings.startSec + span.endSec,
  });
  return {
    title: toSong(times.title),
    name: toSong(times.name),
    endCard: times.endCard === null ? null : toSong(times.endCard),
  };
}

/** True when `sec` is inside a span. */
export function isInSpan(span: { startSec: number; endSec: number } | null, sec: number): boolean {
  return span !== null && sec >= span.startSec && sec < span.endSec;
}

/** The opacity of the black of the fades at `sec` (design d4: "From black", "To black"), 1 s each. */
export function fadeOpacity(settings: MusicVideoDoc['settings'], sec: number): number {
  const fromStart = sec - settings.startSec;
  const toEnd = settings.endSec - sec;
  if (settings.fadeIn && fromStart < 1) {
    return Math.max(0, 1 - Math.max(0, fromStart));
  }
  if (settings.fadeOut && toEnd < 1) {
    return Math.max(0, 1 - Math.max(0, toEnd));
  }
  return 0;
}

/** The first part of the video in which a stopped preview shows the picture under the fade in. */
export const REST_PICTURE_SEC = 0.05;

/**
 * The black of the fades in a preview that does not play. A stopped preview at the start of the
 * video shows the first picture, not the full black of "From black": a black frame looks like a
 * video that did not load. Playback and every other position keep the fade. Pure.
 */
export function restingBlackOpacity(
  black: number,
  settings: Pick<MusicVideoDoc['settings'], 'startSec' | 'fadeIn'>,
  sec: number,
  isRunning: boolean
): number {
  if (isRunning || !settings.fadeIn || sec - settings.startSec >= REST_PICTURE_SEC) {
    return black;
  }
  return 0;
}

/** The length of the white flash of a cut-in `flash` and of the drop flash. */
export const FLASH_SEC = 0.3;
/** The length of a cut-in `fade` from black. */
export const CUT_FADE_SEC = 0.35;
/** The length of a light leak on a section change. */
export const LEAK_SEC = 0.8;

/**
 * The CSS transform of a camera move at `progress` (0..1) of the clip (design c3 "Move"). The
 * preview shows `followFace` still, like the render (`motionOfMove`).
 */
export function moveTransform(move: MusicVideoClip['move'], progress: number): string {
  const p = Math.min(1, Math.max(0, progress));
  switch (move) {
    case 'pushIn':
      return `scale(${(1 + 0.08 * p).toFixed(4)})`;
    case 'pullOut':
      return `scale(${(1.08 - 0.08 * p).toFixed(4)})`;
    case 'panLeft':
      return `scale(1.1) translateX(${(4 - 8 * p).toFixed(3)}%)`;
    case 'panRight':
      return `scale(1.1) translateX(${(-4 + 8 * p).toFixed(3)}%)`;
    case 'still':
    case 'followFace':
      return 'none';
  }
}

/** How far `sec` is into a clip (0..1). */
export function clipProgress(clip: Pick<MusicVideoClip, 'startSec' | 'endSec'>, sec: number): number {
  const length = clip.endSec - clip.startSec;
  return length <= 0 ? 0 : Math.min(1, Math.max(0, (sec - clip.startSec) / length));
}

/** The opacity of the layers over the footage at `sec` (design d3 "On top", c3 "Cut in"). */
export interface EffectOpacities {
  flash: number;
  leak: number;
  /** The black of a fade: the cut-in `fade` and the fades of the video. */
  black: number;
}

function falling(elapsed: number, length: number): number {
  return elapsed >= 0 && elapsed < length ? 1 - elapsed / length : 0;
}

/**
 * The flash on the first beat of each chorus, the light leak on each section change, the cut-in of
 * the clip on screen and the fades of the video. Pure.
 */
export function effectOpacities(
  doc: Pick<MusicVideoDoc, 'look' | 'settings'>,
  songMap: { beats: readonly number[]; sections: readonly MusicVideoSongMapSection[] },
  clip: Pick<MusicVideoClip, 'startSec' | 'cutIn'> | null,
  sec: number
): EffectOpacities {
  let flash = 0;
  if (doc.look.flashOnDrop) {
    for (const section of songMap.sections) {
      if (isChorus(section)) {
        const beat = songMap.beats.find((time) => time >= section.startSec - 0.001) ?? section.startSec;
        flash = Math.max(flash, falling(sec - beat, FLASH_SEC));
      }
    }
  }
  let leak = 0;
  if (doc.look.lightLeaks) {
    for (const section of songMap.sections) {
      const elapsed = sec - section.startSec;
      if (section.startSec > doc.settings.startSec && elapsed >= 0 && elapsed < LEAK_SEC) {
        leak = Math.max(leak, Math.sin((Math.PI * elapsed) / LEAK_SEC));
      }
    }
  }
  let black = fadeOpacity(doc.settings, sec);
  if (clip !== null && clip.startSec > doc.settings.startSec) {
    if (clip.cutIn === 'flash') {
      flash = Math.max(flash, falling(sec - clip.startSec, FLASH_SEC * 0.5));
    } else if (clip.cutIn === 'fade') {
      black = Math.max(black, falling(sec - clip.startSec, CUT_FADE_SEC));
    }
  }
  return { flash, leak, black };
}

/**
 * The one word of the `word` caption style at `songSec`: the line time shared by its words in equal
 * parts (the editor answer has no word times). Null outside the line.
 */
export function captionWordAt(line: MusicVideoLyricLine, nextStartMs: number | null, songSec: number): string | null {
  const words = line.text.split(/\s+/).filter((word) => word !== '');
  if (line.startMs === null || words.length === 0) {
    return null;
  }
  const endMs = line.endMs ?? nextStartMs ?? line.startMs + CAPTION_FALLBACK_SEC * 1000;
  const share = (songSec * 1000 + LYRIC_LINE_LEAD_SECONDS * 1000 - line.startMs) / Math.max(1, endMs - line.startMs);
  const index = Math.min(words.length - 1, Math.max(0, Math.floor(share * words.length)));
  return words[index] ?? null;
}
